import { useMemo, useState } from 'react'

import { usePermission } from '@circlefin/permissions/react'
import { settlementAdvanceSection } from '@services/sections/lib/settlementAdvance'
import { SETTLEMENT_ADVANCE_CREDIT_LINE_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  SettlementCreditLineStatus,
  SettlementCreditLineType,
  useSettlementCreditLinesQuery,
  useSettlementCreditTransferValidationQuery,
} from '@shared/graphql'

import { FloatingLimitsProvider } from '../floating-limits/provider'

import { DEFAULT_VALUES, SettlementCreditLineContext } from './context'

import type { SettlementCreditLineContextState } from './context'
import type { SettlementCreditTransferErrorCodes } from '@shared/graphql'

interface SettlementCreditLineProviderProps {
  /**
   * React Node Children.
   */
  children?: React.ReactNode
  /**
   * Overwrite default values of context. Primarily for testing.
   */
  initValues?: Partial<SettlementCreditLineContextState>
}

export const SettlementCreditLineProvider: React.FC<
  SettlementCreditLineProviderProps
> = ({ children, initValues }) => {
  const [, { isAuthorized, loading: permissionLoading }] = usePermission(
    settlementAdvanceSection.permissions,
  )
  const { data, loading, error, refetch } = useSettlementCreditLinesQuery({
    pollInterval: SETTLEMENT_ADVANCE_CREDIT_LINE_POLL_INTERVAL,
    fetchPolicy: 'cache-and-network',
    skip: !isAuthorized,
  })

  const settlementCreditLine = data?.settlementCreditLines[0]

  const [values] = useState<SettlementCreditLineContextState>({
    ...DEFAULT_VALUES,
    ...initValues,
  })

  const translationKey = useMemo(() => {
    switch (settlementCreditLine?.type) {
      case SettlementCreditLineType.lineOfCredit:
        return 'lineOfCredit'
      case SettlementCreditLineType.settlementAdvance:
      case undefined:
        return 'settlementAdvance'
    }
  }, [settlementCreditLine?.type])

  const {
    data: validation,
    loading: validationLoading,
    error: validationError,
    refetch: validationRefetch,
  } = useSettlementCreditTransferValidationQuery({
    variables: {
      id: settlementCreditLine?.id || '',
    },
    skip: !settlementCreditLine?.id,
  })

  const validationErrors = useMemo(
    () =>
      new Map<SettlementCreditTransferErrorCodes, string>(
        validation?.settlementCreditTransferValidation.map((error) => [
          error.code,
          error.message,
        ]) ?? [],
      ),
    [validation?.settlementCreditTransferValidation],
  )

  const context: SettlementCreditLineContextState = useMemo(() => {
    return {
      ...values,
      loading: loading || permissionLoading || validationLoading,
      settlementCreditLine,
      settlementCreditLineType:
        settlementCreditLine?.type ??
        SettlementCreditLineType.settlementAdvance,
      settlementCreditLineDisabled:
        !settlementCreditLine ||
        settlementCreditLine.status === SettlementCreditLineStatus.inactive,
      translationKey,
      validationErrors,
      refetchSettlementCreditLines: refetch,
    }
  }, [
    values,
    loading,
    permissionLoading,
    validationLoading,
    settlementCreditLine,
    translationKey,
    validationErrors,
    refetch,
  ])

  return (
    <SettlementCreditLineContext.Provider value={context}>
      <FloatingLimitsProvider settlementCreditLine={settlementCreditLine}>
        <GraphQLErrorBoundary error={error} retry={refetch}>
          <GraphQLErrorBoundary
            error={validationError}
            retry={validationRefetch}
          >
            {children}
          </GraphQLErrorBoundary>
        </GraphQLErrorBoundary>
      </FloatingLimitsProvider>
    </SettlementCreditLineContext.Provider>
  )
}
